import { useState, useEffect, useRef } from 'react';
import { message, Upload, UploadProps } from 'antd';
import { RcFile, UploadFile } from 'antd/es/upload';
import { useGetSignedUploadUrlMutation, useGetSignedImageUploadUrlMutation } from '../../../service/service';

export interface UseUploaderProps {
  value?: UploadFile[];
  fileList?: UploadFile[];
  maxSize?: number;
  onChange?: (files: UploadFile[]) => void;
  onFileChange?: (files: UploadFile[]) => void;
  onPreviewFileChange?: (previewFiles: any[]) => void;
  customUpload?: boolean;
  customRequest?: (options: any) => void;
  action?: string;
}

export type MediaType = 'image' | 'video' | 'pdf' | 'document';

export const getMediaType = (file: any): MediaType => {
  const mimeType = (file?.type || file?.raw?.type || '').toLowerCase();
  if (mimeType.startsWith('image/')) return 'image';
  if (mimeType.startsWith('video/')) return 'video';
  if (mimeType === 'application/pdf') return 'pdf';

  const nameOrUrl = (file?.name || file?.url || file?.path || '').toLowerCase();
  if (/\.(jpg|jpeg|png|gif|webp|svg|bmp|ico)$/i.test(nameOrUrl)) return 'image';
  if (/\.(mp4|webm|mov|m4v|avi|mkv|ogv)$/i.test(nameOrUrl)) return 'video';
  if (/\.pdf$/i.test(nameOrUrl)) return 'pdf';

  return 'document';
};

const triggerAnchorDownload = (url: string, filename: string) => {
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.target = '_blank';
  a.rel = 'noopener noreferrer';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};

export const downloadFile = async (file: any, objectUrl?: string) => {
  const downloadUrl = objectUrl || file?.url || file?.path;
  if (!downloadUrl) {
    message.error('No valid URL found for download.');
    return;
  }
  const filename = file?.name || 'download';
  if (downloadUrl.startsWith('blob:')) {
    triggerAnchorDownload(downloadUrl, filename);
    return;
  }
  try {
    const response = await fetch(downloadUrl, { mode: 'cors' });
    if (!response.ok) throw new Error('Fetch failed');
    const blob = await response.blob();
    const tempBlobUrl = URL.createObjectURL(blob);
    triggerAnchorDownload(tempBlobUrl, filename);
    window.setTimeout(() => URL.revokeObjectURL(tempBlobUrl), 1000);
  } catch (error) {
    const win = window.open(downloadUrl, '_blank', 'noopener,noreferrer');
    if (win) win.opener = null;
  }
};

export interface FileInfoDto {
  uid?: string;
  id?: string;
  name?: string;
  path?: string;
  url?: string;
  status?: string;
  isFeatured?: boolean;
  thumb?: string;
  ACL?: string;
}

const hashIdentity = (value: string): string => {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(36);
};

const getLegacyFileFingerprint = (file: any): string =>
  JSON.stringify([
    file?.path || file?.url || '',
    file?.name || '',
    file?.type || file?.raw?.type || '',
    file?.size || file?.raw?.size || '',
    file?.lastModified || file?.raw?.lastModified || '',
  ]);

export const normalizeFileList = (files: any[], uidPrefix = 'file'): UploadFile[] => {
  if (!Array.isArray(files)) return [];

  const fingerprintOccurrences = new Map<string, number>();
  return files.map((file: any) => {
    if (!file) return file;

    const existingUid = file.uid || file.id;
    if (existingUid) {
      return {
        ...file,
        uid: existingUid,
      };
    }

    const fingerprint = getLegacyFileFingerprint(file);
    const occurrence = fingerprintOccurrences.get(fingerprint) || 0;
    fingerprintOccurrences.set(fingerprint, occurrence + 1);

    return {
      ...file,
      uid: `${uidPrefix}-legacy-${hashIdentity(fingerprint)}-${occurrence}`,
    };
  });
};

export const sanitizeFileInfoDto = (file: any): FileInfoDto => {
  const dto: FileInfoDto = {
    uid: file?.uid || file?.id,
    name: file?.name,
    status: file?.status || 'done',
    path: file?.response?.path || file?.path || file?.url || '',
    url: file?.response?.url || file?.url || file?.path || '',
  };
  if (file?.id !== undefined) dto.id = file.id;
  if (file?.isFeatured !== undefined) dto.isFeatured = file.isFeatured;
  if (file?.thumb !== undefined) dto.thumb = file.thumb;
  if (file?.ACL !== undefined) dto.ACL = file.ACL;
  return dto;
};

export const useUploaderLogic = (props: UseUploaderProps) => {
  const {
    value,
    onChange,
    fileList: propFileList,
    maxSize = 5, // Default max size in MB
    onFileChange,
    onPreviewFileChange,
    customUpload = true,
    customRequest,
    action,
  } = props;

  // Track single local blob URL per stable file UID to avoid memory leaks
  const objectUrlMapRef = useRef<Map<string, string>>(new Map());
  // Track active in-flight XHR upload requests to allow clean aborting on removal
  const uploadTaskMapRef = useRef<Map<string, XMLHttpRequest>>(new Map());
  // Track cancelled file UIDs to abort pending signed-URL promises cleanly
  const cancelledUidsRef = useRef<Set<string>>(new Set());
  // Prevent pending signed-URL continuations from starting uploads after unmount
  const isMountedRef = useRef(true);

  const getOrCreateObjectUrl = (uid: string, fileObj?: File | RcFile): string | undefined => {
    if (!uid) return undefined;
    if (objectUrlMapRef.current.has(uid)) {
      return objectUrlMapRef.current.get(uid);
    }
    if (fileObj && typeof window !== 'undefined' && window.URL && typeof window.URL.createObjectURL === 'function') {
      const url = URL.createObjectURL(fileObj);
      objectUrlMapRef.current.set(uid, url);
      return url;
    }
    return undefined;
  };

  const revokeObjectUrl = (uid: string) => {
    if (uid && objectUrlMapRef.current.has(uid)) {
      const url = objectUrlMapRef.current.get(uid);
      if (url && url.startsWith('blob:')) {
        URL.revokeObjectURL(url);
      }
      objectUrlMapRef.current.delete(uid);
    }
  };

  const cancelUploadByUid = (targetUid: string) => {
    if (!targetUid) return;
    cancelledUidsRef.current.add(targetUid);
    if (uploadTaskMapRef.current.has(targetUid)) {
      try {
        uploadTaskMapRef.current.get(targetUid)?.abort();
      } catch (e) {
        // ignore
      }
      uploadTaskMapRef.current.delete(targetUid);
      cancelledUidsRef.current.delete(targetUid);
    }
    revokeObjectUrl(targetUid);
  };

  // Revoke all blob URLs and abort all in-flight uploads on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      uploadTaskMapRef.current.forEach((xhr) => {
        try {
          xhr.abort();
        } catch (e) {
          // ignore
        }
      });
      uploadTaskMapRef.current.clear();

      objectUrlMapRef.current.forEach((url) => {
        if (url && url.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      });
      objectUrlMapRef.current.clear();
    };
  }, []);

  // Initialize fileList from either value (Form.Item control) or fileList prop with normalized UIDs
  const initialRawFiles = value || propFileList || [];
  const [files, setFiles] = useState<UploadFile[]>(() => normalizeFileList(initialRawFiles));
  const filesRef = useRef<UploadFile[]>(files);
  useEffect(() => {
    filesRef.current = files;
  }, [files]);

  // Sync files state when value or fileList prop changes externally (including resets to [] or undefined, and URL decoration updates)
  useEffect(() => {
    const externalRawFiles = value !== undefined ? value : propFileList;
    const currentFiles = filesRef.current;

    if (externalRawFiles !== undefined) {
      const normalizedExternal = normalizeFileList(externalRawFiles);
      const fileUids = currentFiles.map((f: any) => f.uid || f.id);
      const externalUids = normalizedExternal.map((f: any) => f.uid || f.id);
      if (
        JSON.stringify(externalUids) !== JSON.stringify(fileUids) ||
        JSON.stringify(normalizedExternal) !== JSON.stringify(currentFiles)
      ) {
        // Reconcile and cancel uploads for UIDs no longer present in incoming externalFiles
        const externalUidSet = new Set(externalUids);
        fileUids.forEach((uid) => {
          if (uid && !externalUidSet.has(uid)) {
            cancelUploadByUid(uid);
          }
        });

        filesRef.current = normalizedExternal;
        setFiles(normalizedExternal);
      }
    } else {
      // Reconcile and cancel all active uploads when controlled value becomes undefined
      currentFiles.forEach((f: any) => {
        const uid = f.uid || f.id;
        if (uid) cancelUploadByUid(uid);
      });
      filesRef.current = [];
      setFiles([]);
    }
  }, [value, propFileList]);

  // Helper function to check if file is an image based on mime type
  const isFileImage = (file: RcFile): boolean => {
    return file.type.startsWith('image/');
  };

  const beforeUpload = (file: RcFile): boolean | string => {
    const isLessThanMaxSize = file.size / 1024 / 1024 < maxSize;

    if (!isLessThanMaxSize) {
      message.error(`File must be smaller than ${maxSize}MB!`);
      return Upload.LIST_IGNORE;
    }

    return true;
  };

  // RTK Query hooks
  const [getSignedUploadUrl] = useGetSignedUploadUrlMutation();
  const [getSignedImageUploadUrl] = useGetSignedImageUploadUrlMutation();

  // Handle custom upload implementation
  const handleCustomRequest = async (options: any) => {
    if (customRequest) {
      customRequest(options);
    } else if (customUpload) {
      try {
        const { file, onSuccess, onError, onProgress } = options;

        const isImage = isFileImage(file);
        const uid = file.uid || `file-${Date.now()}-${Math.random().toString(36).slice(2)}`;
        const objectUrl = getOrCreateObjectUrl(uid, file);

        const getSignedUrlMutation = isImage ? getSignedImageUploadUrl : getSignedUploadUrl;
        const response = await getSignedUrlMutation({
          fileName: file.name,
          fileType: file.type || 'application/octet-stream',
        }).unwrap();

        if (!isMountedRef.current || cancelledUidsRef.current.has(uid)) {
          cancelledUidsRef.current.delete(uid);
          return;
        }

        const { url, path } = response;

        const fileWithUrl = {
          ...file,
          uid,
          path,
          name: file.name,
          url: path,
          status: 'done',
        };

        if (window.location.hostname === 'localhost' && process.env.NODE_ENV !== 'test') {
          setTimeout(() => {
            onSuccess(fileWithUrl, { url: url });
          }, 100);
          return;
        }

        const xhr = new window.XMLHttpRequest();
        uploadTaskMapRef.current.set(uid, xhr);

        xhr.open('PUT', url);
        xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');

        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            const percent = Math.round((event.loaded / event.total) * 100);
            onProgress({ percent });
          }
        };

        xhr.onload = () => {
          uploadTaskMapRef.current.delete(uid);
          if (xhr.status >= 200 && xhr.status < 300) {
            onSuccess(fileWithUrl, xhr);
          } else {
            const uploadError = new Error(`Upload failed with status ${xhr.status}`);
            message.error('Upload failed');
            options.onError(uploadError);
          }
        };

        xhr.onerror = (err) => {
          uploadTaskMapRef.current.delete(uid);
          message.error('Upload failed');
          options.onError(err);
        };

        xhr.onabort = () => {
          uploadTaskMapRef.current.delete(uid);
        };

        xhr.send(file);
      } catch (error) {
        console.error('Upload error:', error);
        message.error('Upload failed');
        options.onError(error);
      }
    } else {
      const { file, onSuccess } = options;
      setTimeout(() => {
        onSuccess('ok', file);
      }, 1000);
    }
  };

  // Helper to emit clean DTO-compliant payload containing ONLY whitelisted FileInfoDto keys
  const emitFileList = (fileList: UploadFile[]) => {
    const simplifiedFileList = fileList.map((file: any) => sanitizeFileInfoDto(file));

    if (onChange) {
      onChange(simplifiedFileList as any);
    }
    if (onFileChange) {
      onFileChange(simplifiedFileList as any);
    }
    if (onPreviewFileChange) {
      const previewList = fileList.map((file: any) => ({
        ...file,
        uid: file.uid || file.id,
        id: file.id,
        name: file.name,
        status: file.status || 'done',
        path: file.response?.path || file?.path || file?.url || '',
        url: file.response?.url || file?.url || file?.path || '',
        objectUrl: getObjectUrlForUid(file.uid || file.id) || file.objectUrl || file.thumbUrl || file.url || file.path,
        originFileObj: file.originFileObj || file.raw,
      }));
      onPreviewFileChange(previewList);
    }
  };

  const updateFilesAndEmit = (newFiles: UploadFile[]) => {
    filesRef.current = newFiles;
    setFiles(newFiles);
    emitFileList(newFiles);
  };

  const handleChange: UploadProps['onChange'] = ({ fileList: newFileList, file: eventFile }) => {
    const incomingUidSet = new Set(newFileList.map((file: any) => file.uid || file.id).filter(Boolean));

    // Ant Design maxCount replacement omits the previous file without emitting a removed event.
    filesRef.current.forEach((file: any) => {
      const uid = file.uid || file.id;
      if (uid && !incomingUidSet.has(uid)) {
        cancelUploadByUid(uid);
      }
    });

    if (eventFile && eventFile.status === 'removed') {
      cancelUploadByUid(eventFile.uid);
    }

    // Process new local files for object URL creation
    newFileList.forEach((file: any) => {
      if (file.originFileObj && file.uid) {
        getOrCreateObjectUrl(file.uid, file.originFileObj);
      }
    });

    // Map incoming files by UID
    const incomingMap = new Map<string, any>();
    newFileList.forEach((file: any) => {
      const uid = file.uid || file.id;
      if (uid) incomingMap.set(uid, file);
    });

    // Preserve existing user-defined UID order from current files state
    const userOrderUids = filesRef.current.map((f: any) => f.uid || f.id);
    const updatedFiles: any[] = [];

    userOrderUids.forEach((uid) => {
      if (incomingMap.has(uid)) {
        const file = incomingMap.get(uid);
        updatedFiles.push({
          ...file,
          uid,
          name: file.name,
          status: file.status,
          path: file.response?.path || file?.path || file?.url || '',
          url: file.response?.url || file?.url || file?.path || '',
        });
        incomingMap.delete(uid);
      }
    });

    // Append any newly added files not present in existing files state
    incomingMap.forEach((file, uid) => {
      updatedFiles.push({
        ...file,
        uid,
        name: file.name,
        status: file.status,
        path: file.response?.path || file?.path || file?.url || '',
        url: file.response?.url || file?.url || file?.path || '',
      });
    });

    updateFilesAndEmit(updatedFiles as UploadFile[]);
  };

  const addLinkFile = (url: string) => {
    let name: string;
    try {
      const parsedUrl = new URL(url);
      if (!['http:', 'https:'].includes(parsedUrl.protocol)) {
        throw new Error('Only HTTP and HTTPS URLs are allowed.');
      }
      name = parsedUrl.pathname.split('/').pop() || 'file-from-link';
    } catch (error) {
      console.error('Invalid URL provided to addLinkFile:', url, error);
      if (error instanceof Error && error.message.startsWith('Only HTTP')) {
        throw error;
      }
      throw new Error('Invalid URL provided. Please enter a valid URL.');
    }

    const newFile: UploadFile = {
      uid: `link-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      name,
      status: 'done',
      url,
      path: url,
    } as UploadFile;

    const newFiles = Array.isArray(filesRef.current) ? [...filesRef.current, newFile] : [newFile];
    updateFilesAndEmit(newFiles);
  };

  // Reorder files in array by UID
  const reorderFilesByUid = (fromUid: string, toUid: string) => {
    if (!fromUid || !toUid) return;
    const fromIndex = filesRef.current.findIndex((f) => (f.uid || (f as any).id) === fromUid);
    const toIndex = filesRef.current.findIndex((f) => (f.uid || (f as any).id) === toUid);

    if (fromIndex === -1 || toIndex === -1 || fromIndex === toIndex) {
      return;
    }

    const updated = [...filesRef.current];
    const [moved] = updated.splice(fromIndex, 1);
    updated.splice(toIndex, 0, moved);

    updateFilesAndEmit(updated);
  };

  // Move file up or down in array by UID
  const moveFileByUid = (uid: string, direction: 'up' | 'down') => {
    if (!uid) return;
    const index = filesRef.current.findIndex((f) => (f.uid || (f as any).id) === uid);
    if (index === -1) return;
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= filesRef.current.length) return;

    const targetUid = filesRef.current[targetIndex].uid || (filesRef.current[targetIndex] as any).id;
    if (targetUid) {
      reorderFilesByUid(uid, targetUid);
    }
  };

  // Remove file by UID
  const removeFileByUid = (targetUid: string) => {
    if (!targetUid) return;
    cancelUploadByUid(targetUid);

    const updated = filesRef.current.filter((f) => (f.uid || (f as any).id) !== targetUid);
    updateFilesAndEmit(updated);
  };

  const getObjectUrlForUid = (uid: string): string | undefined => {
    return objectUrlMapRef.current.get(uid);
  };

  return {
    files,
    beforeUpload,
    handleCustomRequest,
    handleChange,
    addLinkFile,
    reorderFilesByUid,
    moveFileByUid,
    removeFileByUid,
    getObjectUrlForUid,
    getOrCreateObjectUrl,
    revokeObjectUrl,
  };
};

export default useUploaderLogic;
